import birdsDemo from '@/assets/media/projects/birds.mp4'
import birdsPreview from '@/assets/media/projects/birds_preview.png'
import chickenDemo from '@/assets/media/projects/chicken_meets_worm.mp4'
import chickenPreview from '@/assets/media/projects/chicken_meets_worm_preview.png'
import gridsomeDemo from '@/assets/media/projects/gridsome_plugin.mp4'
import gridsomePreview from '@/assets/media/projects/gridsome_plugin_preview.png'
import pixilzDemo from '@/assets/media/projects/pixilz.mp4'
import pixilzPreview from '@/assets/media/projects/pixilz_preview.png'
import travelogDemo from '@/assets/media/projects/travelog.mp4'
import travelogPreview from '@/assets/media/projects/travelog_preview.png'

const chickenMeetsWorm = {
  title: 'Chicken Meets Worm',
  role: 'Creator & Solo Developer',
  link: {
    src: 'https://chicken-meets-worm.netlify.app/',
    text: 'chicken-meets-worm.netlify.app'
  },
  code: {
    src: 'https://github.com/sammndhr/chicken-meets-worm',
    text: 'github'
  },
  years: { from: 'April 2020', to: 'April 2020' },
  description:
    'Browser JavaScript game made with OOP principles utilizing mouse movements to track and move objects onto an HTML Canvas.',
  bulletPoints: [
    'Designed and built game using Javascript, HTML, Canvas api.',
    "Wrote easing function for smooth animation 'tweening', along with position caching.",
    'Designed and created charming game characters using Adobe Illustrator.'
  ],
  stack: 'Javascript, OOP, Html, Css, Canvas',
  media: { vid: chickenDemo, imgs: [chickenPreview] },
  synop: {
    intro:
      'The game was inspired from a flash game by Orisinal and a meal from Chicken Meets Rice.',
    body:
      "The game utilizes mouse movements to track and paint objects onto an HTML Canvas. All game characters that move inherit from the superclass 'Moving Object'. Data encapsulation is done with ES6 private class fields and select methods use either static or dynamic polymorphism. Character sprites were made with Adobe Photoshop."
  }
}

const travelog = {
  title: 'Travelog',
  role: 'Creator & Full Stack Engineer',
  years: { from: 'January 2020', to: 'March 2020' },
  link: {
    src: 'https://travelog.netlify.app/',
    text: 'travelog.netlify.app'
  },
  code: {
    src: 'https://github.com/sammndhr/travelog',
    text: 'github'
  },
  stack: 'AWS, Express, Vue, Vuex, Vuetify, PSQL, JWT-Auth, Mapbox',
  media: { vid: travelogDemo, imgs: [travelogPreview] },
  description: 'Web app to create a travel diary and map travel pictures',
  synop: {
    intro:
      'Travelog was made because I wanted to log my travel pictures and create a travel diary.',
    body:
      'After the user uploads images, it gets sent to the backend (express), where meta data is parsed through an exif-reader and gets saved to a heroku PSQL database. The images are resized (sharp) and corrected for orientation before being sent to an AWS s3 bucket. The converted images gets sent back to the frontend (Vue) along with the parsed exif data where it gets mapped using Mapbox. Pictures are filtered when the map moves and deleted from account.'
  },
  bulletPoints: [
    'Designed app using Vue for front end and express for backend.',
    'Implemented AWS to store images into database and PSQL to store user and app data.',
    'Utilized jwt-simple module to integrate user authorization with token stored in window.localStorage so users can stay signed in even when browser is quit.'
  ]
}

const pixilz = {
  title: 'Pixilz',
  role: 'Creator & Solo Developer',
  years: { from: 'October 2019', to: 'November 2019' },
  link: {
    src: 'http://pixilz.com',
    text: 'pixilz.com'
  },
  code: {
    src: 'https://github.com/sammndhr/travelog',
    text: 'github'
  },
  stack: 'React, Canvas',
  media: { vid: pixilzDemo, imgs: [pixilzPreview] },
  description: 'Web app for auto stitching and slicing images.',
  synop: {
    intro:
      'Pixilz was created to facilitate editing of vertical scrolling web comic images and is used by ~40 users per month.',
    body:
      'An algorithm checks for similar pixels in row intervals and slices images if it matches certain criteria (average height, pixel match threshold). It uses HTML Canvas API to paint images onto the canvas and check the pixels. Image data urls and canvases are passed between React components with refs and React Context.'
  },
  bulletPoints: [
    'Wrote algorithm using distance formula to calculate how close two pixels are based on their rgb values (images are only sliced if rgb values fall within range).',
    'Maintained ~40 users/month for over a year.'
  ]
}

const birds = {
  title: 'Vue Threejs Birds',
  role: 'Solo Developer',
  link: {
    src: 'https://vue-threejs-birds.netlify.com/',
    text: 'vue-threejs-birds.netlify.com'
  },
  code: {
    src: 'https://www.npmjs.com/package/vue-threejs-birds',
    text: 'npm'
  },
  stack: 'Vue, Threejs, Webgl',
  years: { from: 'January 2020', to: 'January 2020' },
  description: 'Open source Vue plugin to add animated background.',
  media: { vid: birdsDemo, imgs: [birdsPreview] },
  synop: {
    body:
      'The original source comes from Threejs birds flocking animation example. It has been refactored and converted to a Vue module with added options like colors for the birds, animation on scroll.'
  },
  bulletPoints: [
    'Converted Threejs file into vue module that can be added to projects as an npm module.',
    'Added scroll handler to animate birds on scroll.'
  ]
}

const gridsomeSnippet = {
  title: 'Gridsome Plugin',
  role: 'Solo Developer',
  link: {
    src: 'https://gridsome.org/plugins/gridsome-remark-embed-snippet',
    text: 'gridsome-remark-embed-snippet'
  },
  code: {
    src: 'https://www.npmjs.com/package/gridsome-remark-embed-snippet',
    text: 'npm'
  },
  stack: 'Vue, Jest, Gridsome',
  years: { from: 'February 2020', to: 'February 2020' },
  description:
    'Open source Vue Gridsome plugin to embed the contents of specified files as code snippets into markdown files.',
  media: { vid: gridsomeDemo, imgs: [gridsomePreview] },
  synop: {
    body:
      'Markdown lines are visited as nodes using a unist utility tool. If a syntax match is found, the line of text gets parsed to get the file name, which gets read by Node File System and embedded into the markdown file.'
  },
  bulletPoints: [
    'Created plugin to read code files and embed content as snippets into markdown files.'
  ]
}

const ticTacToe = {
  title: 'Tic Tac Toe',
  link: {
    src: 'https://sammndhr.github.io/tictactoe/',
    text: 'tic-tac-toe'
  },
  code: {
    src: 'https://github.com/sammndhr/tictactoe',
    text: 'github'
  },
  media: { vid: null, imgs: [] },
  stack: 'Javascript, Html, Css',
  description: 'Implementation of Tic Tac Toe in javascript.'
}

export { travelog, pixilz, birds, gridsomeSnippet, chickenMeetsWorm, ticTacToe }
